import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ZoneService } from 'src/app/services/modules/instant-rate-manager/zone.service';

@Component({
  selector: 'app-location-cell-editor',
  standalone: true,
  imports: [NgSelectModule, CommonModule, FormsModule],
  templateUrl: './location-cell-editor.component.html',
  styleUrl: './location-cell-editor.component.scss',
})
export class LocationCellEditorComponent implements ICellEditorAngularComp {
  params: any;
  selectedValue: any;
  selectedId: number | null = null;
  items: any[] = [];
  isLoading = false;
  searchTerm$ = new Subject<string>();

  @ViewChild('dropdown') dropdown!: NgSelectComponent;

  private el = inject(ElementRef);
  private zoneService = inject(ZoneService);

  agInit(params: any): void {
    this.params = params;
    this.selectedValue = params.value;

    const width = (this.el.nativeElement as HTMLElement).offsetWidth + 'px';
    document.body.style.setProperty('--dropdown-width', width);

    // Pre-load with current value so it shows immediately
    if (params.value) {
      this.items = [{ value: params.value, label: params.value }];
    }

    // Set up typeahead search
    this.searchTerm$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((term) => this.searchLocations(term));

    // Load initial list
    this.searchLocations('');
  }

  getValue() {
    return this.selectedValue;
  }

  afterGuiAttached?(): void {
    setTimeout(() => {
      this.dropdown.focus();
      this.dropdown.open();
    });
  }

  onChange(item: any) {
    this.selectedValue = item?.label ?? null;
    this.selectedId = item?.value ?? null;

    // Store the selected ID on the row data so onCellValueChanged can access it
    if (this.params.data) {
      const field = this.params.colDef.field;
      const idField = field === 'origin_name' ? 'origin_id' : 'destination_id';
      this.params.data[idField] = this.selectedId;
    }

    setTimeout(() => {
      this.dropdown.blur();
      this.params.api.stopEditing();
    }, 100);
  }

  async searchLocations(term: string) {
    this.isLoading = true;
    try {
      const response = await this.zoneService.getProviderZones({
        name: term || null,
        page_size: 50,
      });
      const data = response?.result ?? response ?? [];
      this.items = Array.isArray(data)
        ? data.map((item: any) => ({ value: item.id, label: item.name }))
        : [];
    } catch {
      this.items = [];
    }
    this.isLoading = false;
  }
}
